import { tournamentsAdapter } from "@/lib/adapters/firestore";
import { Tournament } from "@/types";
import { useQuery } from "@tanstack/react-query";

/**
 * Fetch tournaments for a specific user based on their tournamentIds
 * More efficient than fetching all tournaments and filtering client-side
 */
export const useUserTournaments = (tournamentIds: string[] | undefined) => {
  return useQuery({
    queryKey: ["user-tournaments", tournamentIds],
    queryFn: async () => {
      if (!tournamentIds || tournamentIds.length === 0) {
        return [];
      }

      // Firestore 'in' operator supports up to 10 values
      // If user has more than 10 tournaments, we need to batch
      let tournaments: Tournament[];
      if (tournamentIds.length <= 10) {
        tournaments = await tournamentsAdapter.getAll({
          filters: [
            { field: "__name__", operator: "in", value: tournamentIds },
          ],
        });
      } else {
        const batches: Promise<Tournament[]>[] = [];
        for (let i = 0; i < tournamentIds.length; i += 10) {
          const batch = tournamentIds.slice(i, i + 10);
          batches.push(
            tournamentsAdapter.getAll({
              filters: [{ field: "__name__", operator: "in", value: batch }],
            }),
          );
        }
        const results = await Promise.all(batches);
        tournaments = results.flat();
      }

      return tournaments.filter((t) => !t.archived);
    },
    enabled: Boolean(tournamentIds && tournamentIds.length > 0),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
